import config from '../config';

// Team Management API Service
class TeamManagementApi {
  constructor() {
    this.baseUrl = config.API_URL;
  }

  // Helper method to get current user ID
  getCurrentUserId() {
    // This method is deprecated - use the currentUser parameter instead
    console.warn('getCurrentUserId() is deprecated. Pass currentUser as parameter instead.');
    return null;
  }

  // Helper method to make API requests
  async makeRequest(endpoint, options = {}, currentUser = null) {
    const userId = currentUser?.id || this.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const url = `${this.baseUrl}${endpoint}`;
    
    // Add user_id to query params if not already present
    const urlObj = new URL(url);
    if (!urlObj.searchParams.has('user_id')) {
      urlObj.searchParams.append('user_id', userId);
    }
    
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
      ...options
    };

    try {
      const response = await fetch(urlObj.toString(), defaultOptions);
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  // Team Lineup Management
  async getLineups(leagueId, teamName = null, week = null, currentUser = null) {
    let endpoint = `/leagues/${leagueId}/lineups`;
    const params = new URLSearchParams();
    if (teamName) params.append('team_name', teamName);
    if (week) params.append('week', week);
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }
    
    const response = await this.makeRequest(endpoint, { method: 'GET' }, currentUser);
    return response.lineups || [];
  }

  async saveLineup(leagueId, teamName, week, lineupData, currentUser = null) {
    const endpoint = `/leagues/${leagueId}/lineups`;
    const response = await this.makeRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify({
        team_name: teamName,
        week: week,
        lineup_data: lineupData
      })
    }, currentUser);
    return response.lineup;
  }

  async deleteLineup(leagueId, teamName, week, currentUser = null) {
    const endpoint = `/leagues/${leagueId}/lineups/${encodeURIComponent(teamName)}/${week}`;
    await this.makeRequest(endpoint, { method: 'DELETE' }, currentUser);
  }

  // Team Roster Management
  async getRosters(leagueId, teamName = null, currentUser = null) {
    let endpoint = `/leagues/${leagueId}/rosters`;
    if (teamName) {
      endpoint += `?team_name=${encodeURIComponent(teamName)}`;
    }
    
    const response = await this.makeRequest(endpoint, { method: 'GET' }, currentUser);
    return response.rosters || [];
  }

  async saveRoster(leagueId, teamName, rosterData, currentUser = null) {
    const endpoint = `/leagues/${leagueId}/rosters`;
    const response = await this.makeRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify({
        team_name: teamName,
        roster_data: rosterData
      })
    }, currentUser);
    return response.roster;
  }

  async deleteRoster(leagueId, teamName, currentUser = null) {
    const endpoint = `/leagues/${leagueId}/rosters/${encodeURIComponent(teamName)}`;
    await this.makeRequest(endpoint, { method: 'DELETE' }, currentUser);
  }

  // Team Customization Management
  async getTeamCustomizations(leagueId, currentUser = null) {
    let endpoint = `/leagues/${leagueId}/team-customization`;
    
    const response = await this.makeRequest(endpoint, { method: 'GET' }, currentUser);
    return response.customizations || [];
  }

  async getTeamCustomization(leagueId, teamName, currentUser = null) {
    const customizations = await this.getTeamCustomizations(leagueId, currentUser);
    return customizations.find(c => c.team_name === teamName) || null;
  }

  async saveTeamCustomization(leagueId, teamName, displayName, iconKey, currentUser = null) {
    const endpoint = `/leagues/${leagueId}/team-customization`;
    const response = await this.makeRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify({
        team_name: teamName,
        display_name: displayName,
        icon_key: iconKey
      })
    }, currentUser);
    return response.customization;
  }

  async deleteTeamCustomization(leagueId, teamName, currentUser = null) {
    const endpoint = `/leagues/${leagueId}/team-customization/${encodeURIComponent(teamName)}`;
    await this.makeRequest(endpoint, { method: 'DELETE' }, currentUser);
  }

  // Helper methods for backward compatibility
  async getLineup(leagueId, teamName, week, currentUser = null) {
    const lineups = await this.getLineups(leagueId, teamName, week, currentUser);
    const w = Number(week);
    return (
      lineups.find(
        (lineup) => lineup.team_name === teamName && Number(lineup.week) === w
      )?.lineup_data || null
    );
  }

  async getRoster(leagueId, teamName, currentUser = null) {
    const rosters = await this.getRosters(leagueId, teamName, currentUser);
    return rosters.find(roster => roster.team_name === teamName)?.roster_data || null;
  }

  async getTeamName(leagueId, teamName, currentUser = null) {
    const customizations = await this.getTeamCustomizations(leagueId, currentUser);
    const customization = customizations.find(c => c.team_name === teamName);
    return customization?.display_name || teamName.replace("Team", "Team ");
  }

  async getTeamIcon(leagueId, teamName, currentUser = null) {
    const customizations = await this.getTeamCustomizations(leagueId, currentUser);
    const customization = customizations.find(c => c.team_name === teamName);
    return customization?.icon_key || null;
  }
}

// Create and export a singleton instance
const teamManagementApi = new TeamManagementApi();
export default teamManagementApi;
